import { tap } from 'rxjs/operators/tap';
import { skip } from 'rxjs/operators/skip';
import { distinctUntilChanged } from 'rxjs/operators/distinctUntilChanged';
import { ignoreElements } from 'rxjs/operators/ignoreElements';
import { filter } from 'rxjs/operators/filter';
import { mergeMap } from 'rxjs/operators/mergeMap';
import { skipWhile } from 'rxjs/operators/skipWhile';
import { combineEpics } from 'redux-observable';

import {
  handleCommerceEvent,
  funnelEvent,
  virtualTrackPage,
  languageEvent,
  orderCreatedEvent,
} from './helpers/tracking';

import { SYNC_ROUTE } from '../actions/routes';
import { ORDER_CREATED } from '../actions/transaction';

import CHECKOUT_STEPS_ROUTES from '../routes/checkout';
import CheckoutTrackingQuery from '../graphql/CheckoutTrackingQuery';
import OrderSuccessTrackingQuery from '../graphql/OrderSuccessTrackingQuery';

const isSamePathname = (prev, curr) =>
  prev.location.pathname === curr.location.pathname;

const findCheckoutStepName = (path) =>
  Object.keys(CHECKOUT_STEPS_ROUTES).find(
    (key) => CHECKOUT_STEPS_ROUTES[key].path === path
  );

const trackRouteChangeEpic = (action$) =>
  action$.ofType(SYNC_ROUTE).pipe(
    distinctUntilChanged(isSamePathname),
    skip(1),
    tap((action) => virtualTrackPage(action.location.pathname)),
    ignoreElements()
  );

const trackCheckoutEpic = (action$, store, { apolloClient }) =>
  action$.ofType(SYNC_ROUTE).pipe(
    distinctUntilChanged(isSamePathname),
    filter(({ match: { path } }) => findCheckoutStepName(path)),
    mergeMap((route) =>
      apolloClient
        .query({
          errorPolicy: 'ignore',
          query: CheckoutTrackingQuery,
        })
        .then((response) => [route, response])
    ),
    skipWhile(([, { data }]) => !data?.user?.isAuthenticated),
    tap(([route, { data }]) => {
      const stepName = findCheckoutStepName(route.match.path);
      funnelEvent(stepName, data.user.cart);

      languageEvent(
        route.match.params.language,
        data.user.cart.billingAddress.countryCode
      );

      if (route.match.path === CHECKOUT_STEPS_ROUTES.address.path) {
        handleCommerceEvent(1, data.user.cart);
        funnelEvent('checkout', data.user.cart);
      }
    }),
    ignoreElements()
  );

const trackCheckoutSuccessEpic = (action$, store, { apolloClient }) =>
  action$.ofType(ORDER_CREATED).pipe(
    mergeMap(({ orderNumber }) =>
      apolloClient.query({
        errorPolicy: 'ignore',
        query: OrderSuccessTrackingQuery,
        variables: { orderNumber },
      })
    ),
    tap(
      ({
        data: {
          user: { language, hash, customer, order },
        },
      }) => {
        try {
          return orderCreatedEvent({ language, hash, customer, order });
        } catch (e) {
          // Do nothing
        }
        return null;
      }
    ),
    ignoreElements()
  );

const trackingEpic = combineEpics(
  trackRouteChangeEpic,
  trackCheckoutEpic,
  trackCheckoutSuccessEpic
);

export { trackRouteChangeEpic, trackCheckoutEpic, trackCheckoutSuccessEpic };

export default trackingEpic;
